import { isAxiosError } from 'axios';

import { getSelectedEvaluationTypes, isTemplateRequiredFor } from './evaluationSelection';

import type { EvaluationElementsSelection } from './evaluationSelection';
import type { EvaluationTypeValue } from '@/constants/cycle-evaluation/CycleEvaluation';
import type { TranslationKey } from '@/i18n-key';

const AT_LEAST_ONE_TYPE: TranslationKey =
    'cycleEvaluationEvaluationElements.validation.atLeastOneType';
const TEMPLATE_REQUIRED: TranslationKey =
    'cycleEvaluationEvaluationElements.validation.templateRequired';
const CANNOT_REMOVE_SENT_TYPE: TranslationKey =
    'cycleEvaluationEvaluationElements.validation.cannotRemoveSentType';
export const TEMPLATE_NO_LONGER_AVAILABLE: TranslationKey =
    'cycleEvaluationEvaluationElements.validation.templateNoLongerAvailable';

interface GetEvaluationElementsBlockingMessageParams {
    selection: EvaluationElementsSelection;
    typesWithUnavailableTemplate: readonly EvaluationTypeValue[];
    typesWithUnresolvableTemplate: readonly EvaluationTypeValue[];
}

export const getEvaluationElementsBlockingMessage = ({
    selection,
    typesWithUnavailableTemplate,
    typesWithUnresolvableTemplate,
}: GetEvaluationElementsBlockingMessageParams): TranslationKey | undefined => {
    const selectedTypes = getSelectedEvaluationTypes(selection);

    if (selectedTypes.length === 0) {
        return AT_LEAST_ONE_TYPE;
    }

    const missesARequiredTemplate = selectedTypes.some(
        (type) => isTemplateRequiredFor(type) && !selection.templateIds[type],
    );

    const carriesATemplateNothingCanResolve = selectedTypes.some((type) =>
        typesWithUnresolvableTemplate.includes(type),
    );

    // The lost template names itself on its tile; these two are named nowhere, so they speak first.
    if (missesARequiredTemplate || carriesATemplateNothingCanResolve) {
        return TEMPLATE_REQUIRED;
    }

    return selectedTypes.some((type) => typesWithUnavailableTemplate.includes(type))
        ? TEMPLATE_NO_LONGER_AVAILABLE
        : undefined;
};

export const getEvaluationElementsRemovalBlockingMessage = (
    typesBeingRemoved: EvaluationTypeValue[],
    hasBeenSentByType: Partial<Record<EvaluationTypeValue, boolean>>,
): TranslationKey | undefined =>
    typesBeingRemoved.some((type) => hasBeenSentByType[type]) ? CANNOT_REMOVE_SENT_TYPE : undefined;

export const isDeletedTemplateImportFailure = (error: unknown): boolean =>
    isAxiosError(error) && error.response?.status === 404;
